import * as Rx from 'rxjs';
import { tap } from 'rxjs/operators';
import type { IBuyerRefundReason } from 'v2/components/Shared/RefundReason/buyer';
import type { IPartnerRefundReason } from 'v2/components/Shared/RefundReason/partner';
import type { IRefundMotive, ISendPartialRefund, ISendRefund } from 'v2/interfaces/requestManagement/refund';
import { ERequestType } from 'v2/interfaces/shopping';

import apiService from '../api';
import type { ApiService } from '../api';

class RefundRequestService {
  private loading$ = new Rx.BehaviorSubject<boolean>(false);
  constructor(private apiService: ApiService) {}

  public sendBuyerRefundRequest(refundReason: IBuyerRefundReason) {
    return this.apiService.post('request-management/refund?requester=buyer', {
      request_motive_id: refundReason.motive,
      description: refundReason.detail,
      invoice_id: refundReason.invoiceId
    });
  }

  public sendPartnerRefundRequest(refundReason: IPartnerRefundReason) {
    return this.apiService.post('request-management/refund?requester=partner', {
      request_motive_id: refundReason.motive,
      description: refundReason.detail,
      invoice_id: refundReason.invoiceId
    });
  }

  public getRefundMotive(): Rx.Observable<IRefundMotive[]> {
    this.loading$.next(true);
    return this.apiService
      .get('request-management/motive', {
        type: ERequestType.REQUEST,
        visible: true,
        internal: false
      })
      .pipe(
        tap(response => response.data),
        tap(() => this.loading$.next(false))
      );
  }

  public getRefundMotivesFromBoApi(customerId: number): Rx.Observable<IRefundMotive[]> {
    this.loading$.next(true);
    return this.apiService
      .get('backoffice/refund-motives', {
        customer_id: customerId
      })
      .pipe(
        tap(response => response.data),
        tap(() => this.loading$.next(false))
      );
  }

  public loading() {
    return this.loading$;
  }

  public buyerFindRefundByInvoiceId(invoiceId: string | number) {
    this.loading$.next(true);
    return this.apiService
      .get('request-management/buyer/refund/exists', {
        invoice_id: invoiceId
      })
      .pipe(tap(() => this.loading$.next(false)));
  }

  public partnerFindRefundByInvoiceId(invoiceId: string | number) {
    this.loading$.next(true);
    return this.apiService
      .get('request-management/partner/refund/exists', {
        invoice_id: invoiceId
      })
      .pipe(tap(() => this.loading$.next(false)));
  }

  public sendPartialRefundRequest({ amount, invoice_id, motive_id, description }: ISendPartialRefund) {
    return this.apiService.post(`invoices/${invoice_id}/partialrefund`, {
      amount,
      motive_id,
      description,
      type: 'parcial'
    });
  }

  public sendRefundRequest({ invoice_id, motive_id, motive }: ISendRefund) {
    return this.apiService.post(`invoices/${invoice_id}/refund`, {
      motive_id,
      motive
    });
  }
}

const RefundRequestFactory = new RefundRequestService(apiService);
export const refundRequestFactory = RefundRequestFactory;
